import React from 'react';
import { Link } from 'react-router-dom';
import travelMindsLogo from '../../assets/travelminds-logo-h.webp';

interface Props {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  backHref?: string;
  backLabel?: string;
}

const AuthLayout: React.FC<Props> = ({ title, subtitle, children, footer, backHref, backLabel }) => (
  <div className="public-surface min-h-screen flex flex-col lg:flex-row bg-[var(--bg)]">
    {/* Brand panel — desktop */}
    <aside
      className="hidden lg:flex lg:w-[44%] xl:w-[40%] flex-col justify-between p-12 text-white relative overflow-hidden"
      style={{
        backgroundImage:
          'linear-gradient(140deg, var(--primary) 0%, var(--primary-light) 60%, var(--accent) 130%)',
      }}
    >
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full opacity-20"
        style={{ background: 'radial-gradient(closest-side, white, transparent 70%)' }}
      />
      <div className="relative z-10 max-w-md">
        <h2
          className="text-3xl xl:text-4xl font-semibold leading-tight mb-4"
          style={{ fontFamily: "'Quicksand', sans-serif" }}
        >
          Email marketing for travel agencies.
        </h2>
        <p className="text-base xl:text-lg text-white/80 leading-relaxed">
          Design, send, and measure campaigns under your brand. Built for
          travel advisors managing many agencies — segmentation, analytics,
          and deliverability without the spreadsheets.
        </p>
      </div>

      <div className="relative z-10 text-xs text-white/50">
        © {new Date().getFullYear()} TravelMinds &nbsp;·&nbsp;{' '}
        <a href="https://travelminds.com" className="hover:text-white/80">
          travelminds.com
        </a>
      </div>
    </aside>

    {/* Brand bar — mobile */}
    <header className="lg:hidden border-b border-[var(--border)] bg-white">
      <div className="flex items-center justify-between px-5 py-3">
        <Link to="/" className="flex items-end gap-2">
          <img src={travelMindsLogo} alt="TravelMinds" className="h-8" />
          <span
            className="text-[1rem] text-[var(--accent)] leading-none mb-[4px]"
            style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}
          >
            marketing
          </span>
        </Link>
      </div>
    </header>

    {/* Form panel */}
    <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-12 lg:py-16">
      <div className="w-full max-w-md">
        {backHref && (
          <Link
            to={backHref}
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text)] mb-6 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {backLabel ?? 'Back'}
          </Link>
        )}

        {/* Brand mark — desktop only on the form side (mobile gets the brand bar above) */}
        <Link to="/" className="hidden lg:flex items-end gap-2.5 mb-10">
          <img src={travelMindsLogo} alt="TravelMinds" className="h-12 w-auto" />
          <span
            className="text-[1.15rem] text-[var(--accent)] leading-none mb-[6px]"
            style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}
          >
            marketing
          </span>
        </Link>

        <div className="mb-8">
          <h1
            className="text-3xl sm:text-4xl font-semibold text-[var(--text)] mb-2 tracking-tight"
            style={{ fontFamily: "'Quicksand', sans-serif" }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm sm:text-base text-[var(--text-muted)] leading-relaxed">{subtitle}</p>
          )}
        </div>

        <div className="space-y-5">{children}</div>

        {footer && (
          <div className="mt-8 pt-6 border-t border-[var(--border)] text-sm text-center text-[var(--text-muted)] space-y-2">
            {footer}
          </div>
        )}
      </div>
    </main>
  </div>
);

export default AuthLayout;
